import React from "react";
import { Transaction } from "../../../services/types";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faArrowLeft,
  faArrowRight,
  faSearch,
  faFilter,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { saveAs } from "file-saver";
import removeRedEye from "../../../assets/icons/removeRedEye.svg";
import FoodLoader from "src/components/Loader";
import { format } from "date-fns";
import CustomSelect from "../../../components/ui/CustomSelect";
import TransactionModal from "./TransactionModal";

interface RestaurantTransactionsProps {
  transactions: Transaction[];
  loading: boolean;
  error: string | null;
}

const RestaurantTransactions: React.FC<RestaurantTransactionsProps> = ({
  transactions,
  loading,
  error,
}) => {
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [filter, setFilter] = React.useState<string>("all");
  const [page, setPage] = React.useState<number>(1);
  const [size] = React.useState<number>(10);
  const [selectedTransaction, setSelectedTransaction] =
    React.useState<Transaction | null>(null);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

  const getRestaurantName = (transaction: Transaction): string => {
    if (transaction.restaurant?.name) {
      return transaction.restaurant.name;
    }
    return "N/A";
  };

  const openModal = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedTransaction(null);
    setIsModalOpen(false);
  };

  const downloadCSV = () => {
    if (transactions.length === 0) return;

    const csvData = transactions.map((transaction) => ({
      ID: transaction._id,
      "Restaurant Name": getRestaurantName(transaction),
      Amount: transaction.amount,
      Status: transaction.status,
      Description: transaction.description,
      Type: transaction.type,
      "Transaction Code": transaction.transaction_code,
      "Created At": format(new Date(transaction.created_at), "PPpp"),
      "Updated At": format(new Date(transaction.updated_at), "PPpp"),
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "restaurant_transactions.csv");
  };

  const filterByDateRange = (range: string, transactions: Transaction[]) => {
    const now = new Date();
    let filteredTransactions = transactions;

    if (range === "today") {
      const startOfDay = new Date(now.setHours(0, 0, 0, 0));
      filteredTransactions = transactions.filter(
        (transaction) => new Date(transaction.created_at) >= startOfDay
      );
    } else if (range === "thisWeek") {
      const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
      filteredTransactions = transactions.filter(
        (transaction) => new Date(transaction.created_at) >= startOfWeek
      );
    } else if (range === "thisMonth") {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      filteredTransactions = transactions.filter(
        (transaction) => new Date(transaction.created_at) >= startOfMonth
      );
    } else if (range === "thisYear") {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      filteredTransactions = transactions.filter(
        (transaction) => new Date(transaction.created_at) >= startOfYear
      );
    } else if (range === "debit") {
      filteredTransactions = transactions.filter(
        (transaction) => transaction.type === "debit"
      );
    } else if (range === "credit") {
      filteredTransactions = transactions.filter(
        (transaction) => transaction.type === "credit"
      );
    } else if (range === "successful") {
      filteredTransactions = transactions.filter(
        (transaction) => transaction.status === "successful"
      );
    } else if (range === "unsuccessful") {
      filteredTransactions = transactions.filter(
        (transaction) => transaction.status === "unsuccessful"
      );
    }

    return filteredTransactions;
  };

  const filteredTransactionsByDate = filterByDateRange(filter, transactions);

  const handleSearch = (transactions: Transaction[]) => {
    return transactions.filter((transaction) => {
      const query = searchQuery.toLowerCase();
      const restaurantName = getRestaurantName(transaction).toLowerCase();
      return (
        transaction._id.toLowerCase().includes(query) ||
        restaurantName.includes(query) ||
        transaction.description.toLowerCase().includes(query) ||
        transaction.status.toLowerCase().includes(query) ||
        transaction.type.toLowerCase().includes(query) ||
        transaction.transaction_code?.toLowerCase().includes(query) ||
        transaction.amount.toString().includes(searchQuery)
      );
    });
  };

  const filteredTransactions = handleSearch(filteredTransactionsByDate);
  const paginatedTransactions = filteredTransactions.slice(
    (page - 1) * size,
    page * size
  );
  const totalPages = Math.ceil(filteredTransactions.length / size);

  const statusBadge = (status: string) => {
    const statusClass =
      {
        successful: "bg-green-100 text-green-800",
        unsuccessful: "bg-red-100 text-red-800",
        pending: "bg-yellow-100 text-yellow-800",
      }[status.toLowerCase()] || "bg-gray-100 text-gray-800";

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}
      >
        {status}
      </span>
    );
  };

  const typeBadge = (type: string) => {
    const typeClass =
      {
        debit: "bg-red-100 text-red-800",
        credit: "bg-green-100 text-green-800",
      }[type.toLowerCase()] || "bg-gray-100 text-gray-800";

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${typeClass}`}
      >
        {type}
      </span>
    );
  };

  return (
    <div className="p-6 bg-white rounded-xl shadow-sm border">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Restaurant Transactions
          </h2>
          <p className="text-gray-500 text-sm">
            {transactions.length} total transactions
          </p>
        </div>
        <button
          onClick={downloadCSV}
          disabled={transactions.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FontAwesomeIcon icon={faDownload} />
          Export CSV
        </button>
      </div>

      {loading && (
        <div className="h-[70vh] w-full bg-white bg-opacity-80 flex flex-col items-center justify-center rounded-lg">
          <FoodLoader
            size="md"
            text="Fetching Restaurant Transactions"
            theme="amber"
            className="scale-75"
          />
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          />
        </div>

        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
          </div>
          <CustomSelect
            value={filter}
            onChange={(value) => setFilter(value as string)}
            options={[
              { label: "All Transactions", value: "all" },
              { label: "Today", value: "today" },
              { label: "This Week", value: "thisWeek" },
              { label: "This Month", value: "thisMonth" },
              { label: "This Year", value: "thisYear" },
              { label: "Debit", value: "debit" },
              { label: "Credit", value: "credit" },
              { label: "Successful", value: "successful" },
              { label: "Unsuccessful", value: "unsuccessful" },
            ]}
            placeholder="Filter transactions"
            style={{ paddingLeft: "2.5rem" }}
          />
        </div>
      </div>

      {paginatedTransactions.length === 0 && !loading ? (
        <div className="p-8 text-center bg-gray-50 rounded-lg">
          <p className="text-gray-500">No transactions found</p>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="mt-2 text-amber-600 hover:underline"
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-amber-100/70 text-left text-xs font-semibold text-amber-900 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 border-b border-amber-200">
                    Transaction ID
                  </th>
                  <th className="px-5 py-3 border-b border-amber-200">
                    Restaurant Name
                  </th>
                  <th className="px-5 py-3 border-b border-amber-200">
                    Amount
                  </th>
                  <th className="px-5 py-3 border-b border-amber-200">
                    Status
                  </th>
                  <th className="px-5 py-3 border-b border-amber-200">Type</th>
                  <th className="px-5 py-3 border-b border-amber-200">
                    Description
                  </th>
                  <th className="px-5 py-3 border-b border-amber-200">Date</th>
                  <th className="px-5 py-3 border-b border-amber-200">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-amber-50 divide-y divide-gray-200">
                {paginatedTransactions.map((transaction) => (
                  <tr
                    key={transaction._id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium truncate max-w-[180px]">
                      {transaction._id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm border-b border-amber-100">
                      <span className="font-medium">
                        {getRestaurantName(transaction)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm border-b border-amber-100">
                      <span className="font-medium">
                        ₦{transaction.amount.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm border-b border-amber-100">
                      {statusBadge(transaction.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm border-b border-amber-100">
                      {typeBadge(transaction.type)}
                    </td>
                    <td className="px-6 py-4 text-sm border-b border-amber-100 max-w-[200px] truncate">
                      {transaction.description}
                      {transaction.transaction_code && (
                        <div className="text-xs text-gray-400 mt-1">
                          Code: {transaction.transaction_code}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm border-b border-amber-100">
                      {format(new Date(transaction.created_at), "PPpp")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => openModal(transaction)}
                          className="text-amber-600 hover:text-amber-800 flex items-center gap-1 transition-colors"
                        >
                          <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                          View Details
                        </button>
                        {/* <Link
                          to={`/pr-admin/restaurants/restaurant-details/${transaction.Restaurant_id}`}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 transition-colors"
                        >
                          <img
                            src={removeRedEye}
                            alt="View"
                            className="w-4 h-4"
                          />
                          View Restaurant
                        </Link> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-medium">{(page - 1) * size + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(page * size, filteredTransactions.length)}
              </span>{" "}
              of{" "}
              <span className="font-medium">{filteredTransactions.length}</span>{" "}
              results
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setPage((prevPage) => Math.max(prevPage - 1, 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        page === pageNum
                          ? "bg-amber-500 text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      } transition-colors`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 5 && page < totalPages - 2 && (
                  <span className="px-2">...</span>
                )}
                {totalPages > 5 && page < totalPages - 2 && (
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    {totalPages}
                  </button>
                )}
              </div>
              <button
                onClick={() =>
                  setPage((prevPage) => Math.min(prevPage + 1, totalPages))
                }
                disabled={page === totalPages || totalPages === 0}
                className="px-4 py-2 border border-gray-300 rounded-lg flex items-center gap-2 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Transaction Modal */}
      {selectedTransaction && (
        <TransactionModal
          transaction={selectedTransaction}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default RestaurantTransactions;
